// The bridge "I-beam" glyph — shared by the Pixi board (`PixiGame`) and the
// SVG fortress-card diagrams (`fortressPattern`) so a bridge looks identical in
// both. A web runs along the bridged edge with a perpendicular flange at each
// end; it's stroked twice (a black outline, then a dark-grey fill) so the slim
// beam reads on any background.
//
// The two renderers use different tech (Pixi Graphics vs. SVG), so they can't
// share draw calls — instead they share this geometry + these metrics. All
// sizes are fractions of the bridged edge's length, so the beam is proportioned
// the same whether the edge is a 26px board hex side or a card thumbnail edge.

export type Pt = { x: number; y: number };

/** Canonical bridge colours. Accepted by both Pixi's `stroke({ color })` and
 * SVG's `stroke`. */
export const BRIDGE_OUTLINE_COLOR = "#0e0e11";
export const BRIDGE_FILL_COLOR = "#303036";

/** I-beam metrics as fractions of the bridged edge's length. Tuned on the board
 * (26px edges): inset 7.28px, flange 3px, strokes 2.6px / 1.4px. */
export const I_BEAM = {
  /** Web starts this far in from each endpoint node. */
  inset: 0.28,
  /** Flange half-length. */
  flange: 3 / 26,
  /** Outline (dark) stroke width. */
  outlineWidth: 2.6 / 26,
  /** Fill (grey) stroke width, drawn over the outline. */
  fillWidth: 1.4 / 26,
} as const;

export interface IBeamSegments {
  /** The web, running along the edge. */
  web: [Pt, Pt];
  /** The two perpendicular flanges, one at each web end. */
  flanges: [[Pt, Pt], [Pt, Pt]];
  /** Bridged-edge length — multiply by `I_BEAM.*Width` for stroke widths. */
  len: number;
}

/** The line segments of a bridge I-beam between two node positions, in the same
 * coordinate space as `from`/`to`. `glyphScale` enlarges the flange (the board
 * uses 1; the fortress cards draw the beam larger) — pair it with the same
 * factor on the stroke widths (`I_BEAM.*Width * len * glyphScale`). */
export function iBeamSegments(from: Pt, to: Pt, glyphScale = 1): IBeamSegments {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy; // perpendicular = flange direction
  const ny = ux;
  const inset = len * I_BEAM.inset;
  const fl = len * I_BEAM.flange * glyphScale;
  const a: Pt = { x: from.x + ux * inset, y: from.y + uy * inset };
  const b: Pt = { x: from.x + ux * (len - inset), y: from.y + uy * (len - inset) };
  return {
    web: [a, b],
    flanges: [
      [
        { x: a.x + nx * fl, y: a.y + ny * fl },
        { x: a.x - nx * fl, y: a.y - ny * fl },
      ],
      [
        { x: b.x + nx * fl, y: b.y + ny * fl },
        { x: b.x - nx * fl, y: b.y - ny * fl },
      ],
    ],
    len,
  };
}
